/**
 * Runtime shim for PostHog. Thin by design - every decision lives in the pure
 * `analytics-config.ts`, which is what the tests pin.
 *
 * Reads `import.meta.env` directly rather than going through `config-global.ts`
 * so that analytics owns its own configuration and adds nothing to a file the
 * rest of the app edits.
 */

import {
  buildPosthogOptions,
  nextOrganizationAction,
  shouldEnableAnalytics,
} from "./analytics-config.ts";

// ----------------------------------------------------------------------

type PosthogLike = {
  init: (key: string, options: Record<string, unknown>) => void;
  register: (properties: Record<string, unknown>) => void;
  reset: () => void;
};

let client: PosthogLike | null = null;

const KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY ?? "";
const HOST =
  import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

// ----------------------------------------------------------------------

/**
 * Called once from `main.tsx`, before React mounts. Resolves to nothing and
 * never rejects: analytics must not be able to stop the app rendering.
 */
export async function initAnalytics(): Promise<void> {
  if (!shouldEnableAnalytics(KEY)) return;

  try {
    // Dynamic: with no key configured this module is never fetched or run,
    // so posthog-js stays out of the initial bundle entirely.
    const { default: posthog } = await import("posthog-js");
    posthog.init(KEY, buildPosthogOptions(HOST));
    client = posthog as unknown as PosthogLike;
  } catch {
    // A blocked or broken analytics bundle is not an application error.
    client = null;
  }
}

/**
 * Label subsequent events with the organization, so recordings can be filtered
 * by customer. Does NOT gate recording - everything is captured either way.
 *
 * The id only, never the organization NAME: an opaque id is not
 * customer-identifying in the way a name is, and a name buys nothing a join
 * cannot.
 */
let lastOrganizationId: string | null = null;

/**
 * Label subsequent events with the organization, so recordings are filterable
 * by customer. Does NOT gate recording - everything is captured either way.
 *
 * The id only, never the organization NAME: an opaque id is not
 * customer-identifying in the way a name is, and a name buys nothing a join
 * cannot.
 */
export function setOrganizationContext(args: {
  organizationId: string | null | undefined;
  loading: boolean;
}): void {
  const action = nextOrganizationAction({
    loading: args.loading,
    organizationId: args.organizationId,
    lastOrganizationId,
  });
  if (action === "none") return;

  if (!client) {
    // Remember it anyway, so a later init does not read a stale transition.
    lastOrganizationId = args.organizationId ?? null;
    return;
  }

  try {
    if (action === "register" && args.organizationId) {
      client.register({ organization_id: args.organizationId });
    } else if (action === "reset") {
      // Super properties persist in localStorage, so a genuine sign-out must
      // clear them or a previous user's organization_id rides along on the
      // next user's events on a shared device.
      client.reset();
    }
    lastOrganizationId = args.organizationId ?? null;
  } catch {
    /* never let telemetry break a user flow */
  }
}
